import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Reusable Server-Side Pagination Component
 * Sastra Energy — Back-Office Actualités & Newsletter
 *
 * @param {number} currentPage - Active page index (1-based)
 * @param {number} totalPages - Total number of pages available
 * @param {number} totalItems - Total count of items matching the current filters
 * @param {number} limit - Items per page (10, 20, 50)
 * @param {Function} onPageChange - Callback when user selects a different page (newPage: number) => void
 * @param {Function} onLimitChange - Callback when user selects a different limit (newLimit: number) => void
 * @param {string} itemLabel - Label for plural items, e.g. "articles" or "propositions"
 * @param {boolean} disabled - Whether pagination controls should be disabled (e.g. during loading)
 */
export default function Pagination({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  limit = 10,
  onPageChange,
  onLimitChange,
  itemLabel = 'articles',
  disabled = false,
}) {
  const safeCurrentPage = Math.max(1, currentPage);
  const safeTotalPages = Math.max(1, totalPages || 1);

  // Range calculation (e.g. 1–10 sur 50)
  const startItem = totalItems === 0 ? 0 : (safeCurrentPage - 1) * limit + 1;
  const endItem = totalItems === 0 ? 0 : Math.min(safeCurrentPage * limit, totalItems);

  // Grammatical label: "article" vs "articles"
  const formattedLabel = useMemo(() => {
    if (totalItems <= 1 && itemLabel.endsWith('s')) {
      return itemLabel.slice(0, -1);
    }
    return itemLabel;
  }, [totalItems, itemLabel]);

  // Generate page numbers with ellipsis
  const pageNumbers = useMemo(() => {
    if (safeTotalPages <= 1) return [1];

    // Show all if 7 or fewer pages
    if (safeTotalPages <= 7) {
      return Array.from({ length: safeTotalPages }, (_, i) => i + 1);
    }

    // Near the start
    if (safeCurrentPage <= 4) {
      return [1, 2, 3, 4, 5, 'ellipsis-right', safeTotalPages];
    }

    // Near the end
    if (safeCurrentPage >= safeTotalPages - 3) {
      return [
        1,
        'ellipsis-left',
        safeTotalPages - 4,
        safeTotalPages - 3,
        safeTotalPages - 2,
        safeTotalPages - 1,
        safeTotalPages,
      ];
    }

    // In the middle
    return [
      1,
      'ellipsis-left',
      safeCurrentPage - 1,
      safeCurrentPage,
      safeCurrentPage + 1,
      'ellipsis-right',
      safeTotalPages,
    ];
  }, [safeCurrentPage, safeTotalPages]);

  return (
    <div className="an-table-footer an-pagination-wrapper">
      {/* Left: Range and Per-Page Selector */}
      <div className="an-pagination-left">
        <div className="an-pagination-info">
          {totalItems > 0 ? (
            <span>
              Affichage <strong>{startItem}–{endItem}</strong> sur <strong>{totalItems}</strong> {formattedLabel}
            </span>
          ) : (
            <span>0 {formattedLabel}</span>
          )}
        </div>

        <div className="an-pagination-limit">
          <label htmlFor={`limit-select-${itemLabel}`} className="an-pagination-limit-label">
            Afficher
          </label>
          <select
            id={`limit-select-${itemLabel}`}
            className="an-pagination-limit-select"
            value={limit}
            disabled={disabled}
            onChange={(e) => onLimitChange && onLimitChange(Number(e.target.value))}
            aria-label={`Nombre de ${itemLabel} par page`}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <span className="an-pagination-limit-suffix">par page</span>
        </div>
      </div>

      {/* Right: Page Navigation Controls */}
      <div className="an-pagination-controls" role="navigation" aria-label="Pagination des articles">
        {/* Previous Button */}
        <button
          type="button"
          className="an-pagination-btn an-pagination-nav-btn"
          disabled={safeCurrentPage <= 1 || disabled}
          onClick={() => onPageChange && onPageChange(safeCurrentPage - 1)}
          title="Page précédente"
          aria-label="Page précédente"
        >
          <ChevronLeft size={16} />
          <span>Précédent</span>
        </button>

        {/* Numeric Page Buttons */}
        <div className="an-pagination-pages">
          {pageNumbers.map((p, idx) => {
            if (typeof p === 'string') {
              return (
                <span
                  key={`ellipsis-${p}-${idx}`}
                  className="an-pagination-ellipsis"
                  aria-hidden="true"
                >
                  …
                </span>
              );
            }

            const isActive = p === safeCurrentPage;
            return (
              <button
                key={p}
                type="button"
                className={`an-pagination-btn an-pagination-num ${isActive ? 'active' : ''}`}
                onClick={() => !isActive && !disabled && onPageChange && onPageChange(p)}
                disabled={disabled || isActive}
                aria-current={isActive ? 'page' : undefined}
                title={`Aller à la page ${p}`}
                aria-label={`Page ${p}`}
              >
                {p}
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <button
          type="button"
          className="an-pagination-btn an-pagination-nav-btn"
          disabled={safeCurrentPage >= safeTotalPages || disabled}
          onClick={() => onPageChange && onPageChange(safeCurrentPage + 1)}
          title="Page suivante"
          aria-label="Page suivante"
        >
          <span>Suivant</span>
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

